// besturimz-website/src/components/HeroBanner.js
import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { motion, AnimatePresence } from 'framer-motion'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'

const NAVBAR_HEIGHT = 70
const NAVBAR_HEIGHT_MOBILE = 60

const HeroContainer = styled.section`
  position: relative;
  width: 100%;
  height: calc(100dvh - ${NAVBAR_HEIGHT}px);
  min-height: 420px;
  overflow: hidden;
  margin-top: ${NAVBAR_HEIGHT}px;

  @media (max-width: 768px) {
    height: calc(100dvh - ${NAVBAR_HEIGHT_MOBILE}px);
    min-height: 360px;
    margin-top: ${NAVBAR_HEIGHT_MOBILE}px;
  }
`

const SlideBackground = styled(motion.div)`
  position: absolute;
  inset: 0;
  background-image: url(${({ $bgImage }) => $bgImage});
  background-size: cover;
  background-position: ${({ $bgPosition }) => $bgPosition};
  background-repeat: no-repeat;

  @media (max-width: 768px) {
    background-image: url(${({ $bgImageMobile, $bgImage }) => $bgImageMobile || $bgImage});
    background-position: ${({ $bgPositionMobile, $bgPosition }) =>
      $bgPositionMobile || $bgPosition};
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.18) 0%,
      transparent 38%
    );
  }
`

const TextSideHaze = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  backdrop-filter: blur(16px) saturate(130%);
  -webkit-backdrop-filter: blur(16px) saturate(130%);
  background: radial-gradient(
    ellipse 90% 85% at 22% 50%,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 38%,
    rgba(255, 255, 255, 0.02) 55%,
    transparent 72%
  );
  mask-image: radial-gradient(
    ellipse 95% 88% at 24% 50%,
    rgba(0, 0, 0, 0.92) 0%,
    rgba(0, 0, 0, 0.55) 42%,
    rgba(0, 0, 0, 0.15) 62%,
    transparent 78%
  );
  -webkit-mask-image: radial-gradient(
    ellipse 95% 88% at 24% 50%,
    rgba(0, 0, 0, 0.92) 0%,
    rgba(0, 0, 0, 0.55) 42%,
    rgba(0, 0, 0, 0.15) 62%,
    transparent 78%
  );
`

const ContentPanel = styled.div`
  position: absolute;
  top: 50%;
  left: clamp(24px, 7vw, 96px);
  transform: translateY(-50%);
  z-index: 2;
  max-width: min(520px, 48vw);
  pointer-events: none;
  overflow: hidden;

  @media (max-width: 768px) {
    left: 20px;
    max-width: calc(100% - 96px);
  }
`

const ContentBlock = styled(motion.div)`
  pointer-events: auto;
  text-align: left;
  color: #fff;
`

const SlideTitle = styled.h1`
  font-size: clamp(1.65rem, 3.6vw, 2.75rem);
  font-weight: 700;
  margin: 0 0 0.85rem;
  line-height: 1.15;
  letter-spacing: -0.02em;
  text-shadow:
    0 1px 3px rgba(0, 0, 0, 0.55),
    0 6px 28px rgba(0, 0, 0, 0.45);
`

const SlideSubtitle = styled.p`
  font-size: clamp(0.92rem, 1.6vw, 1.12rem);
  margin: 0 0 1.5rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.95);
  text-shadow:
    0 1px 2px rgba(0, 0, 0, 0.5),
    0 4px 20px rgba(0, 0, 0, 0.35);
  max-width: 46ch;
`

const CTAButton = styled.button`
  background: linear-gradient(135deg, #ef5350 0%, #c62828 100%);
  color: #fff;
  border: none;
  padding: 12px 26px;
  font-size: 0.98rem;
  font-weight: 600;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 16px rgba(198, 40, 40, 0.35);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(198, 40, 40, 0.45);
  }

  &:active {
    transform: translateY(0);
  }
`

const NavigationArrows = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: #fff;

  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }

  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
  }
`

const LeftArrow = styled(NavigationArrows)`
  left: clamp(12px, 2vw, 28px);
`

const RightArrow = styled(NavigationArrows)`
  right: clamp(12px, 2vw, 28px);
`

const slideTransition = { duration: 0.55, ease: [0.45, 0, 0.25, 1] }

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0.85,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction > 0 ? '-100%' : '100%',
    opacity: 0.85,
  }),
}

const contentVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 72 : -72,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction > 0 ? -72 : 72,
    opacity: 0,
  }),
}

const HeroBanner = ({ slides = [] }) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [direction, setDirection] = useState(1)

  useEffect(() => {
    if (slides.length === 0) return

    const interval = setInterval(() => {
      setDirection(1)
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    }, 6000)

    return () => clearInterval(interval)
  }, [slides.length])

  const nextSlide = () => {
    setDirection(1)
    setCurrentSlide((prev) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setDirection(-1)
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
  }

  const handleCtaClick = (href) => {
    if (!href) return

    const id = href.startsWith('#') ? href.slice(1) : href
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      window.history.replaceState(null, '', `#${id}`)
    }
  }

  if (slides.length === 0) {
    return null
  }

  const slide = slides[currentSlide]

  return (
    <HeroContainer>
      <AnimatePresence initial={false} custom={direction}>
        <SlideBackground
          key={`bg-${currentSlide}`}
          custom={direction}
          $bgImage={slide.bgImage}
          $bgImageMobile={slide.bgImageMobile}
          $bgPosition={slide.backgroundPosition || 'center top'}
          $bgPositionMobile={slide.backgroundPositionMobile}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={slideTransition}
        />
      </AnimatePresence>

      <TextSideHaze />

      <ContentPanel>
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <ContentBlock
            key={`content-${currentSlide}`}
            custom={direction}
            variants={contentVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={slideTransition}
          >
            <SlideTitle>{slide.title}</SlideTitle>
            <SlideSubtitle>{slide.subtitle}</SlideSubtitle>
            <CTAButton
              type="button"
              onClick={() => handleCtaClick(slide.ctaHref)}
            >
              {slide.cta}
            </CTAButton>
          </ContentBlock>
        </AnimatePresence>
      </ContentPanel>

      <LeftArrow type="button" onClick={prevSlide} aria-label="Önceki slayt">
        <FaChevronLeft size={18} />
      </LeftArrow>

      <RightArrow type="button" onClick={nextSlide} aria-label="Sonraki slayt">
        <FaChevronRight size={18} />
      </RightArrow>
    </HeroContainer>
  )
}

export default HeroBanner
